import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks';
import {
  getPendingFunding,
  getFundingByDate,
  getFundingDetail,
  confirmFunding,
} from '@/services/credit-payment.service';
import { PARTNER_ID } from '@/constants';

const QUERY_KEYS = {
  pending: ['funding', 'pending'],
  byDate: (date: string) => ['funding', 'byDate', date],
  detail: (id: string) => ['funding', 'detail', id],
} as const;

export function useFunding(date?: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const pendingQuery = useQuery({
    queryKey: QUERY_KEYS.pending,
    queryFn: () => getPendingFunding(),
  });

  const byDateQuery = useQuery({
    queryKey: QUERY_KEYS.byDate(date ?? ''),
    queryFn: () => getFundingByDate(PARTNER_ID, date!),
    enabled: Boolean(date),
  });

  const confirmMutation = useMutation({
    mutationFn: ({ fundingId, bankReference, confirmedBy }: {
      fundingId: string;
      bankReference: string;
      confirmedBy: string;
    }) => confirmFunding(fundingId, { bankReference, confirmedBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pending });
      toast.success('Transferencia confirmada exitosamente');
    },
    onError: () => {
      toast.error('Error al confirmar la transferencia');
    },
  });

  return {
    pending: pendingQuery.data?.data ?? [],
    isLoadingPending: pendingQuery.isLoading,
    isErrorPending: pendingQuery.isError,
    refetchPending: pendingQuery.refetch,

    byDate: byDateQuery.data?.data ?? [],
    isLoadingByDate: byDateQuery.isLoading,

    confirmTransfer: confirmMutation.mutate,
    isConfirming: confirmMutation.isPending,
  };
}

export function useFundingDetail(fundingId: string | undefined) {
  const detailQuery = useQuery({
    queryKey: QUERY_KEYS.detail(fundingId ?? ''),
    queryFn: () => getFundingDetail(fundingId!),
    enabled: Boolean(fundingId),
  });

  return {
    instruction: detailQuery.data?.data ?? null,
    isLoading: detailQuery.isLoading,
    isError: detailQuery.isError,
    refetch: detailQuery.refetch,
  };
}
